import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Popover, List, Typography, Spin, Empty, theme } from 'antd';
import { BellOutlined } from '@ant-design/icons';
import { api } from '@/services/api';
import { API_ENDPOINTS } from '@/constants';
import type { Notification, ApiResult, PaginatedResponse } from '@/types';

const { Text } = Typography;

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function NotificationBell({ size = 36 }: { size?: number }) {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const { token: themeToken } = theme.useToken();

  const { data, isLoading } = useQuery({
    queryKey: ['my-notifications'],
    queryFn: () =>
      api.get<never, ApiResult<PaginatedResponse<Notification>>>(API_ENDPOINTS.MY_NOTIFICATIONS, {
        params: { limit: 20 },
      }),
    refetchInterval: open ? 30000 : false,
  });

  const notifications: Notification[] = data?.data?.data ?? [];
  const unreadCount = notifications.filter((n) => !n.read).length;

  const markReadMutation = useMutation({
    mutationFn: (id: string) =>
      api.patch(`${API_ENDPOINTS.MY_NOTIFICATIONS}/${id}/read`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-notifications'] });
    },
  });

  const content = (
    <div style={{ width: 320, maxHeight: 400, overflowY: 'auto' }}>
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
          <Spin />
        </div>
      ) : notifications.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No notifications yet"
          style={{ padding: '24px 0' }}
        />
      ) : (
        <List
          dataSource={notifications}
          renderItem={(n) => (
            <List.Item
              key={n.id}
              style={{
                padding: '10px 12px',
                cursor: n.read ? 'default' : 'pointer',
                background: n.read ? 'transparent' : '#f0f5ff',
                borderRadius: 8,
                marginBottom: 4,
              }}
              onClick={() => {
                if (!n.read) markReadMutation.mutate(n.id);
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                  <Text strong style={{ fontSize: 13, flex: 1 }}>{n.title}</Text>
                  <Text type="secondary" style={{ fontSize: 11, flexShrink: 0 }}>{timeAgo(n.createdAt)}</Text>
                </div>
                <Text type="secondary" style={{ fontSize: 12 }}>{n.body}</Text>
              </div>
            </List.Item>
          )}
        />
      )}
    </div>
  );

  return (
    <Popover
      content={content}
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Notifications</span>
          {unreadCount > 0 && (
            <Text type="secondary" style={{ fontSize: 12 }}>{unreadCount} unread</Text>
          )}
        </div>
      }
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
      overlayStyle={{ paddingTop: 4 }}
    >
      <Badge count={unreadCount} size="small" offset={[-4, 4]}>
        <Button
          type="text"
          icon={<BellOutlined style={{ fontSize: 18, color: themeToken.colorTextSecondary }} />}
          style={{
            width: size,
            height: size,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
          }}
        />
      </Badge>
    </Popover>
  );
}
